import { Box, Divider, Group, LoadingOverlay, Stack } from '@mantine/core';
import { IconCheck, IconChevronRight } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import H3Medium from 'components/typography/H3Medium';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { groupBy, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { DEVICES_PAGE_PATH, EDIT_DEVICE_PAGE_PATH } from 'routes/paths';
import FaultSeverityLabel from 'components/device/FaultSeverityLabel';
import { DeviceListFaultsResponse } from 'api/actions/device-list-faults/device-list-faults-response';
import P2Medium from 'components/typography/P2Medium';
import P3Regular from 'components/typography/P3Regular';
import { FaultFixValues } from 'components/modals/fix-device-fault/FixDeviceFaultModal';
import { useFixDeviceFault } from 'components/modals/fix-device-fault/FixDeviceFaultProvider';
import { showNotification } from '@mantine/notifications';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { ROLE_ADMIN_ID } from 'model/Role';
import Button from 'components/Button';
import MeasurementContextDisplay from 'components/tables/data/fault/MeasurementContextDisplay';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';

/**
 * Page used to display device's faults.
 *
 */
export default function DeviceFaultsPage() {
  const { deviceId } = useParams();
  const { getAction, roleId } = useApi();
  const { revisions: revisionsModuleEnabled, forms: formsModuleEnabled } = useOrganizationModules();
  const { faultFix } = useFixDeviceFault();
  const [loadData, setLoadData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DeviceListFaultsResponse | undefined>(undefined);
  const [canResolveDeviceFaults, setCanResolveDeviceFaults] = useState(roleId === ROLE_ADMIN_ID);

  const [canResolveDeviceFaultsOnBehalf, setCanResolveDeviceFaultsOnBehalf] = useState(roleId === ROLE_ADMIN_ID);
  const [resolvedFaultIds, setResolvedFaultIds] = useState<number[]>([]);

  const visibleFaults = useMemo(
    () =>
      data?.faults?.filter(({ revision }) => {
        if (!revision) return true;
        return revision.kind === 'revision' ? revisionsModuleEnabled : formsModuleEnabled;
      }),
    [data, formsModuleEnabled, revisionsModuleEnabled]
  );

  const otherFaults = useMemo(() => visibleFaults?.filter((fault) => !fault.revision), [visibleFaults]);

  const revisionFaults = useMemo(() => {
    return groupBy(
      visibleFaults?.filter((fault) => fault.revision),
      'revision.revisionId'
    );
  }, [visibleFaults]);

  /**
   * Marks the fault as fixed in the DB.
   */
  async function handleMarkFaultAsFixed({
    faultId,
    fixedAt,
    fixedNote,
    fixedByUserId,
  }: FaultFixValues & { faultId: number }) {
    await getAction('DeviceMarkFaultFixed')({
      parameters: { deviceId: deviceId!, faultId: String(faultId) },
      payload: { fixedAt, fixedNote, fixedByUserId },
    });
    setResolvedFaultIds((ids) => [...ids, faultId]);
    setLoadData(true);
    showNotification({
      title: 'Nedostatok zariadenia bol vyriešený',
      message: `Nedostatok zariadenia ${data?.deviceName ?? ''} bol označený ako vyriešený.`,
      color: SUCCESS_NOTIFICATION_COLOR,
    });
  }

  /**
   * Opens the modal for marking the device's fault as fixed.
   */
  function markFaultAsFixed(faultId: number) {
    faultFix({
      deviceId: Number(deviceId),
      canResolveDeviceFaults,
      canResolveDeviceFaultsOnBehalf,
      onFaultFix: (values) => handleMarkFaultAsFixed({ faultId, ...values }),
    });
  }

  /**
   * Renders the list of faults - for a single revision, for other faults.
   */
  const renderFaults = useCallback(
    (
      title: string,
      faults: DeviceListFaultsResponse['faults'],
      revision?: { revisionId: number; revisionName: string; finishedAt?: string; kind: string },
      isRevision: boolean = false
    ) => {
      const gridStyle = {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 2fr 1fr 1fr 174px',
        gap: '24px',
        alignItems: 'center',
      };

      return (
        <Stack spacing={0} key={title} pb={48} bg="white.0" sx={{ borderRadius: '4px' }}>
          <Box
            py={24}
            px={16}
            bg="gray.0"
            sx={(theme) => ({ border: `1px solid ${theme.white}`, borderRadius: '4px 4px 0 0' })}
          >
            <Stack spacing={4}>
              {isRevision && (
                <Group position="apart">
                  <P2Regular color="gray.8">{revision!.kind === 'revision' ? 'Revízna správa' : 'Formulár'}</P2Regular>
                  <P2Regular color="gray.8">
                    {revision!.finishedAt
                      ? `Ukončená ${new Date(revision!.finishedAt).toLocaleDateString()}`
                      : revision!.kind === 'revision'
                      ? 'Revízna správa ešte nebola ukončená'
                      : 'Formulár ešte nebol ukončený'}
                  </P2Regular>
                </Group>
              )}
              <H3Medium color="gray.8">{title}</H3Medium>
            </Stack>
            <Divider color="gray.2" my={24} />
            <Box style={gridStyle}>
              <P1Medium color="gray.8">Závažnosť</P1Medium>
              <P1Medium color="gray.8">Názov</P1Medium>
              <P1Medium color="gray.8">Popis</P1Medium>
              <P1Medium color="gray.8">Meranie</P1Medium>
              <P1Medium color="gray.8">Dodatok</P1Medium>
              <P1Medium color="gray.8" pl={12}>
                Riešenie
              </P1Medium>
            </Box>
          </Box>
          {/* if this function is called, the faults will be present */}
          {faults!.map((item) => {
            const isFixed = item.fixed?.fixedAt ? true : false;

            return (
              <Box
                py={24}
                px={16}
                key={item.faultName}
                sx={(theme) => ({
                  background: isFixed ? theme.colors.gray[0] : theme.white,
                  borderBottom: `1px solid ${theme.colors.gray[1]}`,
                  borderLeft: `1px solid ${theme.white}`,
                  borderRight: `1px solid ${theme.white}`,
                })}
              >
                <Box style={gridStyle}>
                  <Box>
                    {item.faultSeverity && (
                      <FaultSeverityLabel
                        faultSeverityId={item.faultSeverity.faultSeverityId}
                        faultSeverityName={item.faultSeverity.faultSeverityName}
                        isFaultFixed={isFixed}
                      />
                    )}
                  </Box>
                  <P2Regular color="gray.8">{item.faultName}</P2Regular>
                  <P2Regular color="gray.8">{item.description}</P2Regular>
                  <Box>
                    <MeasurementContextDisplay measurementContext={item.measurementContext} />
                  </Box>
                  <P2Regular color="gray.8">{item.moreInfo}</P2Regular>
                  <Group position="right">
                    {isFixed ? (
                      <Group spacing={8} py={8} px={16}>
                        <IconCheck stroke="1.5" />
                        <P2Medium>Vyriešený nedostatok</P2Medium>
                      </Group>
                    ) : (canResolveDeviceFaults || canResolveDeviceFaultsOnBehalf) &&
                      !resolvedFaultIds.includes(item.faultId) ? (
                      <Button
                        variant="secondary"
                        rightIcon={<IconChevronRight stroke="1.5" />}
                        onClick={() => markFaultAsFixed(item.faultId)}
                      >
                        Vyriešiť nedostatok
                      </Button>
                    ) : (
                      <></>
                    )}
                  </Group>
                </Box>
                {isFixed && (
                  <Box py={16} pb={24} style={gridStyle}>
                    <Stack>
                      <P3Regular color="gray.6">Dátum riešenia nedostatku</P3Regular>
                      {/* If isFixed === true, the fault has fixedAt prop  */}
                      <P2Regular color="gray.8">{new Date(item.fixed?.fixedAt!).toLocaleDateString()}</P2Regular>
                    </Stack>
                    <Stack>
                      <P3Regular color="gray.6">Vyriešil</P3Regular>
                      <P2Regular color="gray.8">
                        {item.fixed?.fixedBy.fullName ??
                          `${item.fixed?.fixedBy.firstName} ${item.fixed?.fixedBy.lastName}`}
                      </P2Regular>
                    </Stack>
                    <Stack>
                      <P3Regular color="gray.6">Zaznamenal riešenie</P3Regular>
                      <P2Regular color="gray.8">
                        {(item.fixed?.recordedBy ?? item.fixed?.fixedBy)?.fullName ??
                          `${(item.fixed?.recordedBy ?? item.fixed?.fixedBy)?.firstName} ${
                            (item.fixed?.recordedBy ?? item.fixed?.fixedBy)?.lastName
                          }`}
                      </P2Regular>
                    </Stack>
                    <Stack>
                      <P3Regular color="gray.6">Popis riešenia</P3Regular>
                      <P2Regular color="gray.8">{item.fixed?.fixedNote}</P2Regular>
                    </Stack>
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      );
    },
    [data, loadData, canResolveDeviceFaults, canResolveDeviceFaultsOnBehalf, resolvedFaultIds]
  );

  useEffect(() => {
    if (loadData) {
      setLoading(true);

      const deviceFaultsGetAction = getAction('DeviceListFaults');

      deviceFaultsGetAction({ parameters: { deviceId: deviceId! } })
        .then((response) => {
          setData(response);
        })
        .catch(panic)
        .finally(() => {
          setLoading(false);
          setLoadData(false);
        });
    }
  }, [loadData]);

  useEffect(() => {
    if (roleId === ROLE_ADMIN_ID) {
      setCanResolveDeviceFaults(true);
      setCanResolveDeviceFaultsOnBehalf(true);
    } else if (data) {
      const hasPermission = getAction('AuthUserHasPermissionInDepartment');

      Promise.all([
        hasPermission({
          parameters: { departmentId: String(data.department.departmentId), permissionSlug: 'resolve-device-faults' },
        }),
        hasPermission({
          parameters: {
            departmentId: String(data.department.departmentId),
            permissionSlug: 'resolve-device-faults-on-behalf',
          },
        }),
      ])
        .then(([self, onBehalf]) => {
          setCanResolveDeviceFaults(self.hasPermission);
          setCanResolveDeviceFaultsOnBehalf(onBehalf.hasPermission);
        })
        .catch(panic);
    }
  }, [roleId, data]);

  if (!deviceId) {
    return <Navigate to={DEVICES_PAGE_PATH.original} />;
  }

  return (
    <DashboardLayout
      title={`Nedostatky pre zariadenie ${data?.deviceName ?? ''} `}
      breadcrumbs={[
        { title: 'Zariadenia', link: DEVICES_PAGE_PATH.original },
        { title: `${data?.deviceName ?? ''}`, link: EDIT_DEVICE_PAGE_PATH.insert({ deviceId: deviceId! }) },
        { title: 'Nedostatky zariadenia' },
      ]}
    >
      <LoadingOverlay visible={loading} />
      <Stack spacing={24}>
        {revisionFaults &&
          !isEmpty(revisionFaults) &&
          Object.values(revisionFaults).map((items) => {
            // all grouped items have the same revision
            const revision = items[0].revision!;

            return renderFaults(revision.revisionName, items, revision, true);
          })}

        {otherFaults && !isEmpty(otherFaults) && (
          <Box>{renderFaults('Ostatné nedostatky', otherFaults, undefined, false)}</Box>
        )}
      </Stack>

      {!loading && (!visibleFaults || isEmpty(visibleFaults)) && (
        <P2Regular color="gray.7">Zariadenie nemá žiadne nedostatky</P2Regular>
      )}
    </DashboardLayout>
  );
}
